import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {ProleisForMobileState} from '../../../../store/store.index';
import {CoreService, ProleisObject, Resource, Task,} from 'proleis-rest-client';
import {PlanningActions} from '../../../../store/planning/planning.index';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  exhaustMap,
  iif,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  selectPpsFilter,
  selectPpsResourceInfo,
  selectPpsResources,
} from '../../../../store/planning/planning.selectors';
import {PpsFilter, PpsResource,} from '../../../../store/planning/planning.state';
import {ConfirmDialogComponent} from '../../../../general/components/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {map} from 'rxjs/operators';
import {GUID_TYPE_TASK} from '../../../../../p4m/guids';
import {TranslateService} from '@ngx-translate/core';
import {AppConfigService} from "proleis-web-app";
import {
  DEFAULT_TASK_COMPONENT_CONFIG,
  TaskComponentConfig
} from "../../../../general/components/mobile-task/task.config";

export const LAST_SELECTED_RES = 'p4m-last-selected-res';

@Injectable({
  providedIn: 'root',
})
export class PpsService {
  ppsResource$: Observable<PpsResource>;
  selectedResource$: BehaviorSubject<Resource> = new BehaviorSubject<Resource>(
    undefined,
  );
  selectedResId: string;
  resources$: Observable<Resource[]>;
  todoTasks$: Observable<Task[]>;
  inProgressTasks$: Observable<Task[]>;
  finishedTasks$: Observable<Task[]>;
  taskFilter$: Observable<string>;
  ppsFilter: Observable<PpsFilter>;

  constructor(
    private store: Store<ProleisForMobileState>,
    private dialog: MatDialog,
    private coreService: CoreService,
    private translateService: TranslateService,
    private configService: AppConfigService
  ) {
    this.ppsFilter = this.loadPpsFilter();
    if (this.selectedResource$.value) {
      this.selectedResource$.subscribe((res) => {
        if (res) {
          this.selectedResId = res.OBJECT_ID;
        }
      });
    } else {
      this.store.dispatch(PlanningActions.loadPpsResources())
      if (localStorage.getItem(LAST_SELECTED_RES)) {
        this.store.select(selectPpsResources).pipe(
          tap((res) => {
            const selectedRes = res.filter(
              (r) =>
                r.WRAPPED_OBJECT_ID === localStorage.getItem(LAST_SELECTED_RES),
            )[0].OBJECT_ID;
            this.selectedResId = selectedRes.OBJECT_ID;
            this.resourceChangedHandler(selectedRes);
          }),
        );
      }
    }
  }

  loadResources() {
    this.store.dispatch(PlanningActions.loadPpsResources());
    this.resources$ = this.store.select(selectPpsResources).pipe(
      distinctUntilChanged(),
      tap((res) => {
        const lastSelectedRes = localStorage.getItem(LAST_SELECTED_RES);
        if (lastSelectedRes) {
          this.resourceChangedHandler(
            res.find((r) =>
              Number(Number(r.WRAPPED_OBJECT_ID) === Number(lastSelectedRes)),
            ),
          );
        } else {
          this.resourceChangedHandler(res[0]);
        }
      }),
    );
  }

  loadTasks() {
    let fields = [...this.getTaskComponentConfig().headerFields, ...this.getTaskComponentConfig().contentFields];
    fields = fields.filter(f => f.fieldExpression || f.linkIdFieldExpression);
    fields.map(f => `${f.field}=${f.fieldExpression || f.linkIdFieldExpression}`)
    if (this.selectedResId) {
      this.store.dispatch(
        PlanningActions.loadPpsResourceInfoState({resId: this.selectedResId}),
      );
      this.ppsResource$ = this.store.select(
        selectPpsResourceInfo,
        this.selectedResId,
      );
      this.ppsResource$ = combineLatest([
        this.ppsResource$,
        this.taskFilter$,
      ]).pipe(
        exhaustMap(([data, filterExpression]) => {
          return iif(
            () => filterExpression === '',
            of(data),
            this.createFilteredInfo(data, filterExpression),
          );
        }),
        distinctUntilChanged(),
      );

      if (this.ppsResource$) {
        this.filterTasksByStatus();
      }
    }
  }

  setFilter(filter$: Observable<string>) {
    this.taskFilter$ = filter$;
  }

  resourceChangedHandler(res: Resource) {
    if (res) {
      localStorage.setItem(LAST_SELECTED_RES, res.WRAPPED_OBJECT_ID);
      this.selectedResId = res.OBJECT_ID;
      this.selectedResource$.next(res);
    }

    this.loadTasks();
  }

  setTaskInProgress(taskId: string) {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: this.translateService.instant('planning.popups.inProgress'),
          message: this.translateService.instant('planning.popups.confirmInProgress'),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(
            PlanningActions.setTaskInProgress({
              taskId,
              resId: this.selectedResId,
            }),
          );
        }
      });
  }

  setTaskComplete(taskId: string) {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: this.translateService.instant('planning.popups.finishTask'),
          message: this.translateService.instant('planning.popups.confirmFinish'),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(
            PlanningActions.setTaskComplete({
              taskId,
              resId: this.selectedResId,
            }),
          );
        }
      });
  }

  interruptTask(taskId: string) {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: this.translateService.instant('planning.popups.interruptTask'),
          message: this.translateService.instant('planning.popups.confirmInterrupt'),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.store.dispatch(
            PlanningActions.setTaskInterrupted({
              taskId,
              resId: this.selectedResId,
            }),
          );
        }
      });
  }

  isFinished(task: Task) {
    return task.ISTBEARBEITET;
  }

  isInProgress(task: Task) {
    return task.ISTINARBEIT && !task.ISTBEARBEITET;
  }

  isNotReleased(task: Task) {
    return (
      task.FREIGABE_STATUS === 0 &&
      !this.isInProgress(task) &&
      !this.isFinished(task)
    );
  }

  isPartiallyReleased(task: Task) {
    return (
      task.FREIGABE_STATUS < 100 &&
      task.FREIGABE_STATUS !== 0 &&
      !this.isFinished(task) &&
      !this.isInProgress(task)
    );
  }

  isReady(task: Task) {
    return (
      !this.isInProgress(task) &&
      !this.isPartiallyReleased(task) &&
      !this.isNotReleased(task) &&
      !this.isFinished(task)
    );
  }

  isTask(objectId: string): Observable<boolean> {
    return this.coreService
      .getObjectById<ProleisObject>(objectId, 'TYP_GUID=TYP_ID.GUID')
      .pipe(
        map((obj) => {
          if (obj) {
            return obj.TYP_GUID === GUID_TYPE_TASK;
          }
          return false;
        }),
      );
  }

  loadPpsFilter(): Observable<PpsFilter> {
    this.store.dispatch(PlanningActions.loadPpsFilter());
    return this.store.select(selectPpsFilter);
  }

  setPpsFilter(filter: PpsFilter) {
    this.selectedResource$
      .subscribe((res) => {
        this.store.dispatch(
          PlanningActions.setPpsFilter({
            end: filter.end ? filter.end : null,
            start: filter.start ? filter.start : null,
            lead: filter.lead,
            onlyReleased: filter.onlyReleased,
            includeFinished: filter.includeFinished,
            resId: res.OBJECT_ID,
          }),
        );
      })
      .unsubscribe();
  }

  onStartTracking(task: Task) {
    this.store.dispatch(
      PlanningActions.startTimeTracking({
        taskId: task.OBJECT_ID,
      }),
    );
  }

  onStopTracking(task: Task) {
    this.store.dispatch(
      PlanningActions.stopTimeTracking({
        taskId: task.OBJECT_ID,
      }),
    );
  }

  getTaskComponentConfig() {
    const config = this.configService.getConfig().taskComponent;
    if (config) {
      return config as TaskComponentConfig;
    }
    return DEFAULT_TASK_COMPONENT_CONFIG;
  }

  getDynamicAliasFields() {
    const taskComponentConfig = this.getTaskComponentConfig();
    const fields = [...taskComponentConfig.headerFields, ...taskComponentConfig.contentFields];
    const filteredFields = fields.filter(f => f.fieldExpression || f.linkIdFieldExpression);
    const joinedAliasFields = filteredFields.map(f => {
      const aliasFields: string[] = [];
      if (f.fieldExpression) {
        aliasFields.push(`${f.field}=${f.fieldExpression}`);
      }

      if (f.linkIdFieldExpression) {
        aliasFields.push(`${f.linkIdField}=${f.linkIdFieldExpression}`);
      }

      return aliasFields.join(';')
    })
    return joinedAliasFields.join(';');
  }

  getHideIfAlias() {
    const taskComponentConfig = this.getTaskComponentConfig();
    const fields = [...taskComponentConfig.headerFields, ...taskComponentConfig.contentFields];
    const filteredFields = fields.filter(f => f.hideIf);
    return filteredFields.map(f => `HIDEIF_${f.field}=${f.hideIf}`).join(';')
  }

  private createFilteredInfo(
    data: PpsResource,
    filterExpression: string,
  ): Observable<PpsResource> {
    if (data) {
      const headerFields = this.getTaskComponentConfig().headerFields.map(field => field.field);
      const contentFields = this.getTaskComponentConfig().contentFields.map(field => field.field);
      const fields = [...headerFields, ...contentFields];
      const result = {
        resId: data.resId,
        tasks: data.tasks.filter(
          (task) => {
            return fields.some(field => task[field]?.toString().toUpperCase().includes(filterExpression.toUpperCase()));
          }
        ),
      };
      return of(result);
    }
    return null;
  }

  private filterTasksByStatus() {
    this.todoTasks$ = this.ppsResource$.pipe(
      switchMap((data) => {
        if (data && data.tasks) {
          return of(
            data.tasks.filter((task) => {
              return !task.ISTBEARBEITET && !task.ISTINARBEIT;
            }),
          );
        }
        return of([]);
      }),
    );

    this.inProgressTasks$ = this.ppsResource$.pipe(
      switchMap((data) => {
        if (data && data.tasks) {
          return of(
            data.tasks.filter((task) => {
              return task.ISTINARBEIT && !task.ISTBEARBEITET;
            }),
          );
        }
        return of([]);
      }),
    );

    this.finishedTasks$ = this.ppsResource$.pipe(
      switchMap((data) => {
        if (data && data.tasks) {
          return of(
            data.tasks.filter((task) => {
              return task.ISTBEARBEITET;
            }),
          );
        }
        return of([]);
      }),
    );
  }
}
